
        <div class="min-h-screen bg-[color:var(--background)] text-[color:var(--foreground)] pt-20 pb-16 px-4">
            <div class="mx-auto max-w-7xl">
                <div class="text-center mb-12">
                    <h1 class="text-4xl sm:text-5xl font-bold tracking-tight text-[color:var(--foreground)]">Real Estate Projects</h1>
                    <p class="mt-4 text-lg text-[color:var(--muted-foreground)]">Browse our collection of premium developments</p>
                </div>
                
                <ng-container *ngIf="loading">
                    <div class="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        <div *ngFor="let i of [0,1,2,3]" class="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                            <div class="h-36 bg-slate-200 animate-pulse"></div>
                            <div class="p-5 space-y-3">
                                <div class="h-5 bg-slate-200 rounded animate-pulse w-3/4"></div>
                                <div class="space-y-1.5">
                                    <div class="h-3 bg-slate-200 rounded animate-pulse w-full"></div>
                                    <div class="h-3 bg-slate-200 rounded animate-pulse w-5/6"></div>
                                </div>
                                <div class="flex gap-3 pt-2">
                                    <div class="h-6 bg-slate-200 rounded-full animate-pulse w-20"></div>
                                    <div class="h-6 bg-slate-200 rounded-full animate-pulse w-16"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </ng-container>

                <ng-container *ngIf="!loading && error">
                    <div class="max-w-2xl mx-auto rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-[0_22px_50px_rgba(15,23,42,0.10)]">
                        <p class="text-red-600 font-semibold mb-2">Failed to load projects</p>
                        <p class="text-slate-600 mb-6">{{ error }}</p>
                        <button type="button" (click)="fetchProjects()" class="inline-flex items-center justify-center rounded-md bg-[color:var(--primary)] px-4 py-2 font-semibold text-[color:var(--primary-foreground)] transition-colors hover:bg-[color:var(--primary-hover)]">
                            Retry
                        </button>
                    </div>
                </ng-container>

                <ng-container *ngIf="!loading && !error">
                    <div *ngIf="projects.length === 0" class="max-w-2xl mx-auto">
                        <div class="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-[0_22px_50px_rgba(15,23,42,0.10)]">
                            <lucide-icon name="building-2" class="h-16 w-16 text-slate-300 mx-auto mb-4"></lucide-icon>
                            <h3 class="text-xl font-semibold mb-2">No projects found</h3>
                            <p class="text-slate-600">We’re currently adding exciting new projects to our platform.</p>
                        </div>
                    </div>

                    <div *ngIf="projects.length" class="space-y-6">
                        <div class="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
                            <span>Showing {{ projects.length }} of {{ totalCount }} projects</span>
                        </div>
                        <div class="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        <div *ngFor="let project of projects" class="min-w-0 rounded-2xl border border-slate-200 bg-white hover:border-[color:var(--ring)] transition-all shadow-[0_22px_50px_rgba(15,23,42,0.10)] overflow-hidden h-full flex flex-col">
                            <div class="relative h-36 overflow-hidden bg-slate-100 border-b border-slate-200">
                                <img
                                    *ngIf="project.image && !projectImageFailed[project.id]; else projectHeroFallback"
                                    [src]="project.image"
                                    [alt]="project.name || 'Project image'"
                                    class="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
                                    (error)="onProjectImageError(project.id)"
                                />
	                                <ng-template #projectHeroFallback>
	                                    <div class="flex h-full w-full items-center justify-center text-slate-400">
	                                        <lucide-icon name="image" class="h-8 w-8"></lucide-icon>
	                                    </div>
	                                </ng-template>
                                <span class="absolute left-3 top-3 rounded-full bg-black/60 px-3 py-1 text-[11px] font-semibold text-white/80 border border-white/10">
                                    {{ statusLabel(project.status) }}
                                </span>
                            </div>
                            <div class="p-5 gap-4 h-full flex flex-col flex-1">
                                <div class="flex items-start justify-between gap-3">
                                    <div class="min-w-0 flex-1">
                                        <div class="flex flex-wrap items-center gap-2">
                                            <h3 class="text-lg font-semibold text-slate-900 break-words">{{ project.name }}</h3>
                                            <span *ngIf="project.isVerified" class="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
                                                <lucide-icon name="shield-check" class="h-3.5 w-3.5"></lucide-icon>
                                                Verified
                                            </span>
                                        </div>
                                        <a
                                            *ngIf="projectLocationLabel(project) as locationLabel"
                                            [href]="projectLocationLink(project, locationLabel)"
                                            target="_blank"
                                            rel="noreferrer"
                                            class="flex min-w-0 items-center gap-2 text-sm text-slate-600 hover:text-[color:var(--primary)]"
                                        >
                                            <lucide-icon name="map-pin" class="h-4 w-4 shrink-0 text-[#6e9bad]"></lucide-icon>
                                            <span class="truncate min-w-0 flex-1">{{ locationLabel }}</span>
                                            <lucide-icon name="external-link" class="h-3.5 w-3.5 shrink-0 text-slate-400"></lucide-icon>
                                        </a>
                                        <p *ngIf="projectCountryLabel(project)" class="text-xs text-slate-500 mt-1">{{ projectCountryLabel(project) }}</p>
                                        <div *ngIf="partnerDisplayName(project) || project.organizationName" class="mt-3 flex items-center gap-2">
                                            <div class="h-9 w-9 overflow-hidden rounded-full border border-slate-200 bg-slate-50 flex items-center justify-center">
                                                <img
                                                    *ngIf="project.vendorLogo && !partnerLogoFailed[project.id]; else partnerLogoFallback"
                                                    [src]="project.vendorLogo"
                                                    [alt]="(project.organizationName || partnerDisplayName(project) || 'Partner')"
                                                    class="h-full w-full object-cover"
                                                    (error)="onPartnerLogoError(project.id)"
                                                />
                                                <ng-template #partnerLogoFallback>
                                                    <lucide-icon name="users" class="h-4 w-4 text-slate-400"></lucide-icon>
                                                </ng-template>
                                            </div>
                                            <div class="min-w-0">
                                                <p *ngIf="partnerDisplayName(project)" class="text-xs font-semibold text-slate-700 truncate">{{ partnerDisplayName(project) }}</p>
                                                <p *ngIf="project.organizationName && project.organizationName !== partnerDisplayName(project)" class="text-[11px] text-slate-500 truncate">{{ project.organizationName }}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <span class="shrink-0 rounded-full bg-[#aa9072]/10 text-[#aa9072] border border-[#aa9072]/25 text-xs font-semibold px-3 py-1">
                                        {{ statusLabel(project.status) }}
                                    </span>
                                </div>

                                <p class="text-sm text-slate-600 line-clamp-3" *ngIf="project.description; else noDesc">
                                    {{ project.description }}
                                </p>
                                <ng-template #noDesc>
                                    <p class="text-sm text-slate-500 italic">No description available</p>
                                </ng-template>

                                <div class="flex flex-wrap gap-2">
                                    <span *ngFor="let highlight of project.highlights || []" class="rounded-full bg-slate-100 border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700">
                                        {{ highlight }}
                                    </span>
                                    <span *ngIf="project.propertyCount" class="rounded-full bg-slate-100 border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700">
                                        {{ project.propertyCount }} units
                                    </span>
                                </div>

                                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-slate-700">
                                    <div class="flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
                                        <lucide-icon name="calendar-clock" class="h-4 w-4 text-[#6e9bad]"></lucide-icon>
                                        <div>
                                            <p class="text-[11px] uppercase tracking-wide text-slate-500">Delivery</p>
                                            <p class="font-semibold">{{ project.deliveryStatus || 'Schedule TBD' }}</p>
                                        </div>
                                    </div>
                                    <div class="flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
                                        <lucide-icon name="wallet" class="h-4 w-4 text-[#6e9bad]"></lucide-icon>
                                    <div>
                                        <p class="text-[11px] uppercase tracking-wide text-slate-500">Starting price</p>
                                            <p class="font-semibold">{{ startingPriceLabel(project) || 'Contact for pricing' }}</p>
                                        </div>
                                    </div>
                                </div>

                                <div class="mt-auto">
	                                    <a class="inline-flex w-full items-center justify-center rounded-md bg-[color:var(--primary)] px-4 py-2 text-sm font-semibold text-[color:var(--primary-foreground)] transition-colors hover:bg-[color:var(--primary-hover)]" [routerLink]="['/projects', project.id]">
	                                        View Details
	                                    </a>
                                </div>
                            </div>
                        </div>
                        </div>

                        <div *ngIf="hasNextPage" class="flex justify-center">
                            <button
                                type="button"
                                (click)="loadMore()"
                                [disabled]="loadingPage"
                                class="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
                            >
                                <lucide-icon [name]="loadingPage ? 'loader-2' : 'plus'" class="h-4 w-4" [class.animate-spin]="loadingPage"></lucide-icon>
                                {{ loadingPage ? 'Loading...' : 'Show more' }}
                            </button>
                        </div>
                    </div>
                </ng-container>
            </div>
        </div>
    
